import { useState, useEffect } from 'react'
import { Calendar, User, MessageCircle, PlusCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import BlogPostCreate from '../components/BlogPostCreate'

export default function Blog() {
  const [posts, setPosts] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    loadBlogPosts()
  }, [])

  const loadBlogPosts = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/blog`)
      const data = await response.json()
      if (data.success) {
        setPosts(data.data.posts)
      }
    } catch (error) {
      console.error('Error loading blog posts:', error)
      // No fallback - show empty state instead
      setPosts([])
    } finally {
      setLoading(false)
    }
  }

  const handlePostSuccess = (newPost) => {
    setPosts(prev => [newPost, ...prev])
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="content-container p-8 mb-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Community Blog
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Stories, traditions, and insights from the Kapcheboin community
            </p>
          </div>
        </div>

        {/* Create Post Button */}
        {isAuthenticated && (
          <div className="mb-8 text-center">
            <button 
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center space-x-2 mx-auto"
            >
              <PlusCircle className="w-5 h-5" />
              <span>Write a Post</span>
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="content-container p-12">
            <div className="flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          </div>
        )}

        {/* Blog Posts */}
        {!loading && (
          <div className="space-y-8">
            {posts.map((post) => (
              <article key={post.id} className="card-glass hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center space-x-4 mb-4">
                  <span className="inline-block bg-primary-100 text-primary-800 text-sm px-3 py-1 rounded-full font-medium">
                    {post.category}
                  </span>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(post.publishDate).toLocaleDateString()}
                  </div>
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 mb-3 hover:text-primary-600 cursor-pointer transition-colors">
                  {post.title}
                </h2>
                
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 font-medium">{post.author}</span>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1 text-gray-500">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm">{post.commentCount || 0}</span>
                    </div>
                    <button className="text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors">
                      Read More
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && posts.length === 0 && (
          <div className="content-container p-12">
            <div className="text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <PlusCircle className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                No blog posts yet
              </h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Be the first to share your story with the Kapcheboin community.
              </p>
              {isAuthenticated ? (
                <button 
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary"
                >
                  Write the First Post
                </button>
              ) : (
                <p className="text-sm text-gray-500">
                  <a href="/login" className="text-primary-600 hover:text-primary-700 font-medium">Sign in</a> to write blog posts
                </p>
              )}
            </div>
          </div>
        )}

        {/* Pagination placeholder */}
        {!loading && posts.length > 0 && (
          <div className="content-container p-6 mt-8">
            <div className="flex justify-center">
              <div className="flex space-x-2">
                <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                  Previous
                </button>
                <button className="px-4 py-2 bg-primary-600 text-white rounded-md">
                  1
                </button>
                <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                  2
                </button>
                <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Post Modal */}
        <BlogPostCreate
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handlePostSuccess}
        />
      </div>
    </div>
  )
}